import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2 } from "lucide-react";
import api from "@/lib/api";

/**
 * SearchDropdown — debounced live search that queries /api/search and renders a grouped
 * dropdown (news · blogs · IPOs). Powers the masthead search bar.
 */
export default function SearchDropdown() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [res, setRes] = useState({ news: [], blogs: [], ipos: [] });
  const rootRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 2) { setRes({ news: [], blogs: [], ipos: [] }); return; }
    setLoading(true);
    const h = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(q)}`);
        setRes({ news: r.data.news || [], blogs: r.data.blogs || [], ipos: r.data.ipos || [] });
      } catch (_) { setRes({ news: [], blogs: [], ipos: [] }); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(h);
  }, [q]);

  useEffect(() => {
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };
  const total = res.news.length + res.blogs.length + res.ipos.length;

  return (
    <div ref={rootRef} className="relative w-full" data-testid="header-search-root">
      <div className="w-full flex items-center gap-2 bg-white border border-[#E2DDD3] rounded-full px-3.5 py-2 focus-within:border-[#D91E2A]">
        {loading ? <Loader2 className="w-3.5 h-3.5 text-[#8C8D90] animate-spin" /> : <Search className="w-3.5 h-3.5 text-[#8C8D90]" />}
        <input
          type="text"
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onKeyDown={(e) => { if (e.key === "Enter" && q.trim()) go(`/search?q=${encodeURIComponent(q.trim())}`); if (e.key === "Escape") setOpen(false); }}
          placeholder="Search companies, news, IPOs"
          className="bg-transparent outline-none text-sm text-[#1A1A1A] placeholder:text-[#8C8D90] w-full"
          data-testid="header-search"
        />
      </div>

      {open && q.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-full mt-2 ns-card max-h-[480px] overflow-y-auto z-50 shadow-lg" data-testid="search-dropdown">
          {total === 0 && !loading && (
            <div className="p-5 text-sm text-[#8C8D90] font-mono-fin text-center">No matches for "{q}"</div>
          )}

          {res.news.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-1 ns-eyebrow border-b border-[#E2DDD3]">News · {res.news.length}</div>
              {res.news.slice(0, 6).map(n => (
                <button
                  key={n.slug}
                  onClick={() => go(`/news/${n.slug}`)}
                  className="w-full text-left px-4 py-2.5 hover:bg-[#FAF8F3] border-b border-[#E2DDD3] last:border-0 flex gap-3 items-start"
                  data-testid={`search-news-${n.slug}`}
                >
                  {n.image && <img src={n.image} alt="" className="w-10 h-10 object-cover rounded flex-shrink-0" loading="lazy" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[#D91E2A]">{n.source} · {n.category}</div>
                    <div className="text-sm text-[#1A1A1A] font-semibold leading-snug line-clamp-2">{n.title}</div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {res.blogs.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-1 ns-eyebrow border-b border-[#E2DDD3]">Blogs · {res.blogs.length}</div>
              {res.blogs.slice(0, 4).map(b => (
                <button
                  key={b.slug}
                  onClick={() => go(`/blogs/${b.slug}`)}
                  className="w-full text-left px-4 py-2.5 hover:bg-[#FAF8F3] border-b border-[#E2DDD3] last:border-0"
                  data-testid={`search-blog-${b.slug}`}
                >
                  <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[#D91E2A]">{b.category}</div>
                  <div className="text-sm text-[#1A1A1A] font-semibold leading-snug">{b.title}</div>
                  {b.excerpt && <div className="text-xs text-[#6B6C6F] mt-0.5 line-clamp-1">{b.excerpt}</div>}
                </button>
              ))}
            </div>
          )}

          {res.ipos.length > 0 && (
            <div className="py-2">
              <div className="px-4 py-1 ns-eyebrow border-b border-[#E2DDD3]">IPOs · {res.ipos.length}</div>
              {res.ipos.slice(0, 4).map(i => (
                <button
                  key={i.id}
                  onClick={() => go("/ipos")}
                  className="w-full text-left px-4 py-2.5 hover:bg-[#FAF8F3] border-b border-[#E2DDD3] last:border-0"
                  data-testid={`search-ipo-${i.id}`}
                >
                  <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[#D91E2A]">{i.status}</div>
                  <div className="text-sm text-[#1A1A1A] font-semibold">{i.company_name}</div>
                  <div className="text-xs text-[#6B6C6F]">{i.price_band} · {i.open_date}</div>
                </button>
              ))}
            </div>
          )}

          {total > 0 && (
            <button
              onClick={() => go(`/search?q=${encodeURIComponent(q.trim())}`)}
              className="w-full text-center px-4 py-2.5 bg-[#FAF8F3] text-xs font-semibold text-[#D91E2A] hover:text-[#B8141F] border-t border-[#E2DDD3]"
              data-testid="search-view-all"
            >
              View all results →
            </button>
          )}
        </div>
      )}
    </div>
  );
}
